import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    TextField,
    Button,
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Link,
    CircularProgress,
    makeStyles
} from '@material-ui/core';
import { Person, CreditCard } from '@material-ui/icons';
import { useStateValue } from '../../context/state/provider';
import { WIZARD_STEPS } from '../../constants/wizardSteps.constants';
import { VIEW } from '../../constants/view.constants';
import { publishAgent, sendVerificationCode, verifyEmailCode } from '../../services/api';
import { checkEmailSubscription } from '../../services/emailSubscriptionCheck';

const useStyles = makeStyles((theme) => ({
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minHeight: 400
    },
    title: {
        marginBottom: theme.spacing(2),
        textAlign: 'center'
    },
    description: {
        marginBottom: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(1.5)
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(1)
        }
    },
    textField: {
        flex: 1
    },
    fullWidthField: {
        marginBottom: theme.spacing(2)
    },
    infoBox: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1.5)
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 'auto'
    },
    backButton: {
        minWidth: 120,
        padding: theme.spacing(1.5, 3),
        backgroundColor: '#ffffff',
        color: theme.palette.primary.main,
        border: `2px solid ${theme.palette.primary.main}`,
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main
        }
    },
    publishButton: {
        minWidth: 120
    },
    customTooltip: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.common.white,
        fontSize: '12px',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(1),
        maxWidth: 200
    },
    tooltipArrow: {
        display: 'none'
    },
    prefilledEmailBox: {
        padding: theme.spacing(2.5),
        backgroundColor: '#ffffff', // White background for maximum contrast
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(2),
        border: `2px solid #059669`, // Green border for success state
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(5, 150, 105, 0.15)' // Green shadow
    },
    prefilledEmailText: {
        color: '#111827', // Very dark gray/black for maximum readability
        fontWeight: 600,
        fontSize: '16px'
    },
    prefilledEmailSubtext: {
        color: '#059669', // Green for success message
        fontSize: '14px',
        marginTop: theme.spacing(0.5),
        fontWeight: 500
    },
    adminBadge: {
        backgroundColor: '#059669', // Green background to match theme
        color: 'white',
        padding: theme.spacing(0.75, 1.5),
        borderRadius: theme.spacing(2),
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)'
    },
    verificationSection: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: '#f8f9fa',
        borderRadius: theme.spacing(1),
        border: '1px solid #e9ecef'
    },
    verificationTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#495057',
        marginBottom: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start'
        }
    },
    codeInputContainer: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(1)
        }
    },
    codeInput: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            fontSize: '18px',
            fontFamily: '"Courier New", monospace',
            letterSpacing: '2px',
            textAlign: 'center'
        }
    },
    verifyButton: {
        minWidth: 120,
        height: 56,
        backgroundColor: '#007bff',
        color: 'white',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#0056b3'
        },
        '&:disabled': {
            backgroundColor: '#6c757d'
        }
    },
    resendButton: {
        minWidth: 100,
        height: 56,
        color: '#6c757d',
        borderColor: '#6c757d',
        '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#495057',
            color: '#495057'
        }
    },
    sendCodeButton: {
        backgroundColor: '#17a2b8',
        color: 'white',
        fontWeight: 600,
        padding: theme.spacing(1.5, 3),
        '&:hover': {
            backgroundColor: '#138496'
        },
        '&:disabled': {
            backgroundColor: '#6c757d'
        }
    },
    verificationSuccess: {
        padding: theme.spacing(1.5),
        backgroundColor: '#d4edda',
        border: '1px solid #c3e6cb',
        borderRadius: theme.spacing(0.5),
        color: '#155724',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    verificationError: {
        padding: theme.spacing(1.5),
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: theme.spacing(0.5),
        color: '#721c24',
        marginTop: theme.spacing(1)
    },
    verificationInstructions: {
        fontSize: '14px',
        color: '#6c757d',
        marginBottom: theme.spacing(2),
        lineHeight: 1.5
    },
    publishingOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        gap: theme.spacing(2)
    },
    publishingMessage: {
        fontSize: '24px',
        fontWeight: 600,
        color: theme.palette.primary.main
    }
}));

export default function UserDetailsStep() {
    const classes = useStyles();
    const [{ wizardData }, dispatch] = useStateValue();
    const [userDetails, setUserDetails] = useState(wizardData.userDetails || {
        firstName: '',
        lastName: '',
        email: '',
        company: ''
    });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const containerRef = useRef(null);

    // Prevent auto-scroll on component mount
    useEffect(() => {
        if (containerRef.current) {
            // Scroll to the top of the container
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, []);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setUserDetails(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };



    const handleNeedHelp = () => {
        // Save current form data
        dispatch({
            type: 'UPDATE_WIZARD_DATA',
            data: { userDetails }
        });
        
        // Store current step for return navigation
        dispatch({
            type: 'SET_PREVIOUS_STEP',
            step: WIZARD_STEPS.USER_DETAILS
        });
        dispatch({
            type: 'SET_CURRENT_STEP',
            step: WIZARD_STEPS.WHITE_GLOVE_SERVICE
        });
    };

    // Email verification handlers - Enhanced with subscription check
    const handleSendVerificationCode = async () => {
        if (!validateEmail(userDetails.email)) {
            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    error: 'Please enter a valid email address first'
                }
            });
            return;
        }

        dispatch({
            type: 'UPDATE_EMAIL_VERIFICATION',
            data: {
                loading: true,
                error: null,
                message: 'Checking your account...'
            }
        });

        try {
            // Step 1: Check if email exists in subscription table (only if no JWT token)
            let existingClientId = wizardData.clientId; // Use JWT clientId if available
            
            if (!existingClientId) {
                console.log('[EMAIL_VERIFICATION] No JWT token found, checking for existing subscription...');
                
                try {
                    const subscriptionCheck = await checkEmailSubscription(userDetails.email);
                    
                    if (subscriptionCheck.data?.isExistingClient) {
                        existingClientId = subscriptionCheck.data.clientId;
                        console.log(`[EMAIL_VERIFICATION] Found existing client: ${existingClientId}`);
                        
                        // Store the found clientId in wizard data
                        dispatch({
                            type: 'UPDATE_WIZARD_DATA',
                            data: {
                                clientId: existingClientId,
                                isExistingClient: true,
                                clientName: subscriptionCheck.data.clientName,
                                packageName: subscriptionCheck.data.packageName
                            }
                        });
                        
                        // SECURITY: Always require email verification for non-JWT users
                        // Even if existing subscription found, must verify email ownership
                        dispatch({
                            type: 'UPDATE_EMAIL_VERIFICATION',
                            data: {
                                isVerified: false,
                                codeSent: false,
                                loading: false,
                                message: `Welcome back! Sending verification code to confirm your ${subscriptionCheck.data.packageName} subscription.`
                            }
                        });
                        
                        console.log('[EMAIL_VERIFICATION] Existing subscription found, but still requiring email verification for security');
                    } else {
                        console.log('[EMAIL_VERIFICATION] No existing subscription found, proceeding as new user');
                        dispatch({
                            type: 'UPDATE_EMAIL_VERIFICATION',
                            data: {
                                isVerified: false,
                                codeSent: false,
                                loading: false,
                                message: 'New user detected, sending verification code...'
                            }
                        });
                    }
                } catch (subscriptionError) {
                    // If it's a disposable email error, don't proceed - throw it to outer catch
                    if (subscriptionError.message?.startsWith('DISPOSABLE_EMAIL:')) {
                        throw subscriptionError; // Re-throw to be caught by outer error handler
                    }
                    
                    // For other subscription check errors, proceed with verification
                    console.warn('[EMAIL_VERIFICATION] Subscription check failed, proceeding with verification:', subscriptionError);
                    dispatch({
                        type: 'UPDATE_EMAIL_VERIFICATION',
                        data: {
                            isVerified: false,
                            codeSent: false,
                            loading: false,
                            message: 'Sending verification code...'
                        }
                    });
                }
            }

            // Step 2: Send verification code (always required for security when no JWT token)
            console.log('[EMAIL_VERIFICATION] Sending verification code...');
            
            const response = await sendVerificationCode({
                email: userDetails.email,
                clientId: existingClientId || wizardData.clientId
            });

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    codeSent: true,
                    sessionId: response.data.sessionId,
                    // SECURITY FIX: Only trust isAdminPanelUser if user came from admin panel with JWT
                    // Don't allow backend to bypass verification for users with existing subscriptions
                    isAdminPanelUser: wizardData.prefilledFromAdmin || false,
                    error: null,
                    message: null
                }
            });

            console.log('✅ Verification code sent successfully');
        } catch (error) {
            console.error('❌ Failed to send verification code:', error);
            
            let errorMessage = 'Failed to send verification code. Please try again.';
            
            if (error.message.startsWith('RATE_LIMITED:')) {
                const parts = error.message.split(':');
                const minutes = parts[1];
                errorMessage = `Too many requests. Please try again in ${minutes} minutes.`;
            } else if (error.message.startsWith('VALIDATION_ERROR:')) {
                errorMessage = error.message.split(':')[1];
            } else if (error.message.startsWith('TIMEOUT:')) {
                errorMessage = 'Request timed out. Please try again.';
            } else if (error.message.startsWith('DISPOSABLE_EMAIL:')) {
                // Extract the user-friendly message from the backend
                errorMessage = error.message.split(':')[1];
            }

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    error: errorMessage,
                    message: null
                }
            });
        }
    };

    const handleVerifyCode = async () => {
        const code = wizardData.emailVerification.verificationCode;
        
        if (!code || code.length !== 6) {
            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    error: 'Please enter a valid 6-digit code'
                }
            });
            return;
        }

        dispatch({
            type: 'UPDATE_EMAIL_VERIFICATION',
            data: {
                loading: true,
                error: null
            }
        });

        try {
            await verifyEmailCode({
                email: userDetails.email,
                code: code,
                sessionId: wizardData.emailVerification.sessionId
            });

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    isVerified: true,
                    error: null
                }
            });

            console.log('✅ Email verified successfully');
        } catch (error) {
            console.error('❌ Failed to verify code:', error);
            
            let errorMessage = 'Invalid verification code. Please try again.';
            
            if (error.message.startsWith('RATE_LIMITED:')) {
                const parts = error.message.split(':');
                const minutes = parts[1];
                errorMessage = `Too many attempts. Please try again in ${minutes} minutes.`;
            } else if (error.message.startsWith('CODE_EXPIRED:')) {
                errorMessage = error.message.split(':')[1];
                // Reset to allow sending new code
                dispatch({
                    type: 'UPDATE_EMAIL_VERIFICATION',
                    data: {
                        codeSent: false,
                        verificationCode: '',
                        sessionId: null
                    }
                });
            } else if (error.message.startsWith('INVALID_CODE:')) {
                const parts = error.message.split(':');
                const message = parts[1];
                const remaining = parts[2];
                errorMessage = `${message}${remaining ? ` (${remaining} attempts remaining)` : ''}`;
            } else if (error.message.startsWith('VALIDATION_ERROR:')) {
                errorMessage = error.message.split(':')[1];
            }

            dispatch({
                type: 'UPDATE_EMAIL_VERIFICATION',
                data: {
                    loading: false,
                    error: errorMessage
                }
            });
        }
    };

    const handleCodeInputChange = (event) => {
        const value = event.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
        dispatch({
            type: 'UPDATE_EMAIL_VERIFICATION',
            data: {
                verificationCode: value,
                error: null // Clear error when user types
            }
        });
    };

    const handlePublish = async () => {
        const newErrors = {};

        // Validation - Only Email and Terms for now
        /* Commented out for now - First Name, Last Name, and Company validation
        if (!userDetails.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!userDetails.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        */
        // Only validate email if not pre-filled from admin panel
        if (!wizardData.prefilledFromAdmin) {
            if (!userDetails.email.trim()) {
                newErrors.email = 'Email is required';
            } else if (!validateEmail(userDetails.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        // Email verification validation - Skip for admin panel users
        if (!wizardData.emailVerification.isVerified && !wizardData.emailVerification.isAdminPanelUser) {
            newErrors.general = 'Please verify your email address before publishing your agent.';
        }
        /* Commented out for now - Company validation
        if (!userDetails.company.trim()) {
            newErrors.company = 'Company name is required';
        }
        */
        if (!agreedToTerms) {
            newErrors.terms = 'Please agree to the terms and conditions';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setIsLoading(true);
            
            // Prepare comprehensive publish data
            const publishData = {
                // Agent Information
                chatbotId: wizardData.chatbotId,
                chatbotName: wizardData.chatbotName,
                skillDescription: wizardData.skillDescription,
                chatbotUrl: wizardData.chatbotUrl,
                
                // Client ID for admin panel integration
                clientId: wizardData.clientId || null,
                prefilledFromAdmin: wizardData.prefilledFromAdmin || false,
                currentAgents: wizardData.currentAgents || 0,
                
                // User Details - Only email for now
                userDetails: {
                    /* Commented out for now
                    firstName: userDetails.firstName,
                    lastName: userDetails.lastName,
                    company: userDetails.company,
                    */
                    email: userDetails.email
                },
                
                // Content Information
                files: wizardData.files.map(file => ({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    uploadedAt: file.uploadedAt || new Date().toISOString()
                })),
                totalFiles: wizardData.files.length,
                
                // Publishing Configuration
                status: 'online',
                publishedAt: new Date().toISOString(),
                agreedToTerms,
                
                // Additional Metadata
                wizardVersion: '1.0',
                createdAt: wizardData.createdAt || new Date().toISOString(),
                lastModified: new Date().toISOString(),
                
                // Optional fields for future use
                webhookUrl: userDetails.webhookUrl || null,
                customDomain: userDetails.customDomain || null,
                analyticsEnabled: true,
                publicAccess: true
            };

            // Call comprehensive publish API (it will handle both BigQuery and Firestore updates)
            console.log('Publishing agent with comprehensive data...');
            const publishResponse = await publishAgent(publishData);
            
            console.log('🔍 DEBUG: Full publishResponse:', publishResponse);
            console.log('🔍 DEBUG: publishResponse.data:', publishResponse.data);
            
            // Save user details and publish results
            dispatch({
                type: 'UPDATE_WIZARD_DATA',
                data: { 
                    userDetails,
                    isPublished: true,
                    publishedAt: new Date().toISOString(),
                    publishResponse: publishResponse.data
                }
            });

            // Navigate to success page
            dispatch({
                type: 'SET_VIEW',
                view: VIEW.SUCCESS
            });
            
        } catch (error) {
            console.error('Error publishing agent:', error);
            
            // Extract user-friendly error message from API response
            let errorMessage = 'Failed to publish agent. Please try again.';
            let isSubscriptionExpired = false;
            let isTokenExpired = false;
            
            if (error.message) {
                // Use the specific error message from the API
                errorMessage = error.message;
            } else if (error.response?.data?.message) {
                // Fallback to response data message
                errorMessage = error.response.data.message;
            }
            
            // Check if this is a JWT token expiration error
            if (error.response?.status === 401 && 
                (errorMessage.toLowerCase().includes('wizard token has expired') ||
                 errorMessage.toLowerCase().includes('token has expired') ||
                 errorMessage.toLowerCase().includes('expired wizard token') ||
                 error.response?.data?.wizardTokenError?.includes('expired'))) {
                isTokenExpired = true;
                errorMessage = 'Your wizard session has expired (1 hour limit). Please go back to the admin panel and generate a new wizard link to publish your agent.';
            }
            
            // Check if this is a subscription expiration error
            if (error.response?.data?.subscriptionExpired === true || 
                errorMessage.toLowerCase().includes('subscription has expired') ||
                errorMessage.toLowerCase().includes('subscription expired')) {
                isSubscriptionExpired = true;
            }
            
            setErrors({ 
                general: errorMessage,
                subscriptionExpired: isSubscriptionExpired,
                tokenExpired: isTokenExpired
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box ref={containerRef} className={classes.stepContainer}>
            {/* Publishing Overlay */}
            {isLoading && (
                <Box className={classes.publishingOverlay}>
                    <CircularProgress size={60} />
                    <Typography className={classes.publishingMessage}>
                        Publishing Agent...
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Please wait while we publish your agent
                    </Typography>
                </Box>
            )}

            <Typography variant="h6" className={classes.title}>
                Publish Your Agent
            </Typography>
            
            <Typography variant="body1" className={classes.description}>
                You're almost done! Enter and verify your email address to publish your AI Agent.
            </Typography>

            <Card className={classes.formCard}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        <Person style={{ verticalAlign: 'middle', marginRight: 8 }} />
                        Contact Information
                    </Typography>

                    {/* Commented out for now - First Name and Last Name fields
                    <Box className={classes.formRow}>
                        <TextField
                            label="First Name"
                            variant="outlined"
                            value={userDetails.firstName}
                            onChange={handleInputChange('firstName')}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            className={classes.textField}
                            required
                        />
                        <TextField
                            label="Last Name"
                            variant="outlined"
                            value={userDetails.lastName}
                            onChange={handleInputChange('lastName')}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            className={classes.textField}
                            required
                        />
                    </Box>
                    */}

                    {wizardData.prefilledFromAdmin ? (
                        <Box className={classes.prefilledEmailBox}>
                            <Box>
                                <Typography variant="body1" className={classes.prefilledEmailText}>
                                    {userDetails.email}
                                </Typography>
                                <Typography variant="body2" className={classes.prefilledEmailSubtext}>
                                    ✓ Authenticated from Admin Panel
                                </Typography>
                            </Box>
                            <Box className={classes.adminBadge}>
                                Admin User
                            </Box>
                        </Box>
                    ) : (
                        <TextField
                            label="Email Address"
                            variant="outlined"
                            type="email"
                            value={userDetails.email}
                            onChange={handleInputChange('email')}
                            error={!!errors.email}
                            helperText={errors.email}
                            className={classes.fullWidthField}
                            fullWidth
                            required
                        />
                    )}

                    {/* Commented out for now - Company Name field
                    <TextField
                        label="Company Name"
                        variant="outlined"
                        value={userDetails.company}
                        onChange={handleInputChange('company')}
                        error={!!errors.company}
                        helperText={errors.company}
                        className={classes.fullWidthField}
                        fullWidth
                        required
                    />
                    */}

                    {/* Email Verification Section - Only show for non-admin panel users */}
                    {!wizardData.emailVerification.isAdminPanelUser && (
                        <Box className={classes.verificationSection}>
                            <Box className={classes.verificationTitle}>
                                <Typography style={{ fontSize: '16px', fontWeight: 600, color: '#495057' }}>
                                    📧 Email Verification Required
                                </Typography>
                                
                                {/* Send Code Button - only show when not verified and code not sent and email is valid */}
                                {!wizardData.emailVerification.isVerified && 
                                 !wizardData.emailVerification.codeSent && 
                                 validateEmail(userDetails.email) && (
                                    <Button
                                        variant="contained"
                                        onClick={handleSendVerificationCode}
                                        disabled={wizardData.emailVerification.loading}
                                        className={classes.sendCodeButton}
                                    >
                                        {wizardData.emailVerification.loading ? 'Sending...' : 'Send Verification Code'}
                                    </Button>
                                )}
                            </Box>

                            {/* Success State */}
                            {wizardData.emailVerification.isVerified && (
                                <Box className={classes.verificationSuccess}>
                                    <span style={{ fontSize: '18px' }}>✅</span>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                        Email verified successfully! You can now publish your agent.
                                    </Typography>
                                </Box>
                            )}

                            {/* Code Input State */}
                            {!wizardData.emailVerification.isVerified && wizardData.emailVerification.codeSent && (
                                <>
                                    <Typography variant="body2" style={{ marginBottom: 16, color: '#495057', fontWeight: 500 }}>
                                        📬 Check your email for a 6-digit verification code
                                    </Typography>
                                    
                                    <Box className={classes.codeInputContainer}>
                                        <TextField
                                            label="Enter 6-digit code"
                                            variant="outlined"
                                            value={wizardData.emailVerification.verificationCode}
                                            onChange={handleCodeInputChange}
                                            placeholder="123456"
                                            className={classes.codeInput}
                                            inputProps={{
                                                maxLength: 6,
                                                style: { textAlign: 'center' }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleVerifyCode}
                                            disabled={wizardData.emailVerification.verificationCode.length !== 6 || wizardData.emailVerification.loading}
                                            className={classes.verifyButton}
                                        >
                                            {wizardData.emailVerification.loading ? 'Verifying...' : 'Verify'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleSendVerificationCode}
                                            disabled={wizardData.emailVerification.loading}
                                            className={classes.resendButton}
                                        >
                                            Resend
                                        </Button>
                                    </Box>
                                </>
                            )}

                            {/* Error Display */}
                            {wizardData.emailVerification.error && (
                                <Box className={classes.verificationError}>
                                    <Typography variant="body2">
                                        <strong>⚠️ Error:</strong> {wizardData.emailVerification.error}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Admin Panel User Confirmation */}
                    {wizardData.emailVerification.isAdminPanelUser && (
                        <Box className={classes.verificationSuccess}>
                            <span style={{ fontSize: '18px' }}>✅</span>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                Email verified via admin panel authentication. You can now publish your agent.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Box className={classes.infoBox}>
                <Typography variant="body2">
                    <strong>🚀 What happens next:</strong>
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                        <li>Your agent will be published and accessible via a public URL</li>
                        <li>You'll receive setup instructions and integration guides</li>
                        <li>Analytics and usage reports will be available in your dashboard</li>
                        <li>You can update and improve your agent anytime</li>
                    </ul>
                </Typography>
            </Box>

            <Card className={classes.formCard}>
                <CardContent>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreedToTerms}
                                onChange={(e) => {
                                    setAgreedToTerms(e.target.checked);
                                    if (errors.terms) {
                                        setErrors(prev => ({ ...prev, terms: '' }));
                                    }
                                }}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I agree to the{' '}
                                <a href="https://sail.cx/terms-and-conditions-of-use/" target="_blank" rel="noopener noreferrer">
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a href="https://sail.cx/privacy-policy/" target="_blank" rel="noopener noreferrer">
                                    Privacy Policy
                                </a>
                            </Typography>
                        }
                    />
                    {errors.terms && (
                        <Typography color="error" variant="body2" style={{ marginTop: 4 }}>
                            {errors.terms}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Display general errors */}
            {errors.general && (
                <Box style={{ 
                    margin: '16px 0', 
                    padding: '16px', 
                    backgroundColor: '#ffebee', 
                    border: '1px solid #f44336', 
                    borderRadius: '8px',
                    textAlign: 'left'
                }}>
                    <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: '8px' }}>
                        <strong>❌ Error</strong>
                    </Typography>
                    <Typography variant="body2" style={{ color: '#d32f2f', marginBottom: (errors.subscriptionExpired || errors.tokenExpired) ? '16px' : '0' }}>
                        {errors.general}
                    </Typography>
                    
                    {/* Show renewal button for subscription expiration */}
                    {errors.subscriptionExpired && (
                        <Box style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                            <Link
                                href="https://sail.cx/pricing/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#218838',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 8px rgba(40, 167, 69, 0.4)'
                                    }
                                }}
                            >
                                <CreditCard fontSize="small" />
                                Renew Subscription
                            </Link>
                        </Box>
                    )}
                    
                    {/* Show "Generate New Link" button for token expiration */}
                    {errors.tokenExpired && (
                        <Box style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    // Clear the expired token and guide user
                                    sessionStorage.removeItem('wizardToken');
                                    window.alert('Please go to your admin panel to generate a new wizard link. Your session expired after 1 hour for security.');
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 16px',
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
                                }}
                            >
                                🔗 Generate New Link
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            <Box className={classes.buttonContainer}>
                <Tooltip 
                    title="Click here if you need support"
                    classes={{ tooltip: classes.customTooltip, arrow: classes.tooltipArrow }}
                    placement="top"
                >
                    <Button
                        variant="outlined"
                        onClick={handleNeedHelp}
                        className={classes.backButton}

                    >
                        Need help?
                    </Button>
                </Tooltip>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePublish}
                    className={classes.publishButton}
                    disabled={isLoading || (!wizardData.emailVerification.isVerified && !wizardData.emailVerification.isAdminPanelUser)}
                >
                    {isLoading ? 'Publishing...' : 
                     (!wizardData.emailVerification.isVerified && !wizardData.emailVerification.isAdminPanelUser) ? 'Verify Email to Publish' : 
                     'Publish Agent'}
                </Button>
            </Box>
        </Box>
    );
}
